import { RefObject, useCallback, useLayoutEffect, useRef } from 'react';
import { zoomLevelStore } from '../store/zoomLevel';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';

const DRAG_INCREMENT_ON_EDGE = 10;

export const useTrimDrags = ({
  setDraggingElement,
  setDraggingElementOffset,
  onDragEnd = () => Promise.resolve(),
  draggingElementOffset,
  droppableContainerRef,
  draggableElementParentRef,
  shouldFixDraggableElement = false,
  isDragFromEnd = false,
  fps
}: {
  setDraggingElement: (dragId: string | null) => void;
  setDraggingElementOffset: (offset: number) => void;
  draggingElementOffset: number;
  onDragEnd?: (offset: number, dragId: string) => void;
  droppableContainerRef?: RefObject<HTMLDivElement>;
  draggableElementParentRef?: RefObject<HTMLDivElement>;
  shouldFixDraggableElement?: boolean;
  isDragFromEnd?: boolean;
  fps: number;
}) => {
  const marginOffsetRef = useRef(0);
  useLayoutEffect(() => {
    if (draggableElementParentRef?.current) {
      if (isDragFromEnd) {
        draggableElementParentRef.current.style.paddingRight = `calc(${-1 * draggingElementOffset * fps}px * var(--timeline-zoom))`;
        draggableElementParentRef.current.style.marginLeft = `calc(${marginOffsetRef.current * fps}px * var(--timeline-zoom))`;
      } else {
        draggableElementParentRef.current.style.marginLeft = `calc(${(marginOffsetRef.current + draggingElementOffset) * fps}px * var(--timeline-zoom))`;
      }
    }
  }, [draggableElementParentRef, draggingElementOffset, isDragFromEnd, fps]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, dragElementId: string, maxOffset = Infinity, minOffset = -Infinity) => {
      e.preventDefault();
      e.stopPropagation();
      let storedDragValue = 0;
      let edgeIncrementalId;
      marginOffsetRef.current = 0;

      document.body.style.cursor = 'ew-resize';
      EventBus.dispatch(CustomEvents.NewTimelineEntityDraggingStateChanged, { isDraggingTimelineEntity: true });

      let initialPosition = e.clientX;
      setDraggingElement(dragElementId);
      setDraggingElementOffset(0);
      let lastValidOffset = 0;

      const isValidDrag = (timeOffset: number) => {
        const isValid = timeOffset >= minOffset && timeOffset <= maxOffset;
        return isValid;
      };

      const isDropInsideParent = (e: MouseEvent) => {
        if (!droppableContainerRef) {
          return true;
        }
        const rect = droppableContainerRef.current?.getBoundingClientRect();
        if (rect) {
          const isDropInside = e.clientY >= rect.top && e.clientY <= rect.bottom;
          return isDropInside;
        }
        return false;
      };

      const updateDragValues = (e: MouseEvent) => {
        const zoomLevel = zoomLevelStore.getSnapshot().zoomLevel;
        let timeOffset = (e.clientX - initialPosition) / zoomLevel / fps;
        if (edgeIncrementalId) {
          clearTimeout(edgeIncrementalId);
          edgeIncrementalId = null;
        }

        if (
          timeOffset + storedDragValue === lastValidOffset &&
          shouldFixDraggableElement &&
          draggableElementParentRef?.current?.parentElement
        ) {
          const parentRect = draggableElementParentRef.current.parentElement.getBoundingClientRect();
          const dragIncrement = DRAG_INCREMENT_ON_EDGE / zoomLevel / fps;
          if (e.clientX <= parentRect.left) {
            if (isValidDrag(timeOffset + storedDragValue - dragIncrement)) {
              storedDragValue += timeOffset - dragIncrement;
              initialPosition = e.clientX;
              timeOffset = 0;
              marginOffsetRef.current += dragIncrement;
            }
            edgeIncrementalId = setTimeout(() => {
              updateDragValues(e);
            }, 50);
          } else if (e.clientX >= parentRect.right) {
            if (isValidDrag(timeOffset + storedDragValue + dragIncrement)) {
              storedDragValue += timeOffset + dragIncrement;
              initialPosition = e.clientX;
              timeOffset = 0;
              marginOffsetRef.current -= dragIncrement;
            }
            edgeIncrementalId = setTimeout(() => {
              updateDragValues(e);
            }, 50);
          }
        }

        if (!isValidDrag(timeOffset + storedDragValue)) {
          if (timeOffset + storedDragValue > maxOffset) {
            timeOffset = maxOffset - storedDragValue;
          } else if (timeOffset + storedDragValue < minOffset) {
            timeOffset = minOffset - storedDragValue;
          }
        }
        lastValidOffset = timeOffset + storedDragValue;
        setDraggingElementOffset(storedDragValue + timeOffset);
      };

      const onMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        updateDragValues(e);
      };

      const onMouseUp = async (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        document.body.style.cursor = 'default';
        const zoomLevel = zoomLevelStore.getSnapshot().zoomLevel;
        EventBus.dispatch(CustomEvents.NewTimelineEntityDraggingStateChanged, { isDraggingTimelineEntity: false });
        const offset = e.clientX - initialPosition;
        const timeOffset = offset / zoomLevel / fps;
        setDraggingElement(null);
        if (isDropInsideParent(e)) {
          if (isValidDrag(offset + storedDragValue)) {
            await onDragEnd(timeOffset + storedDragValue, dragElementId);
          } else {
            await onDragEnd(lastValidOffset, dragElementId);
          }
        }
        setDraggingElementOffset(0);
        onCleanUp();
      };

      const onCleanUp = () => {
        setTimeout(() => {
          if (draggableElementParentRef?.current) {
            draggableElementParentRef.current.style.marginLeft = '0px';
            draggableElementParentRef.current.style.paddingRight = '0px';
          }
        }, 0);
        if (draggableElementParentRef?.current) {
          draggableElementParentRef.current.style.marginLeft = '0px';
          draggableElementParentRef.current.style.paddingRight = '0px';
        }

        if (edgeIncrementalId) {
          clearTimeout(edgeIncrementalId);
          edgeIncrementalId = null;
        }
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      return onCleanUp;
    },
    [
      setDraggingElement,
      setDraggingElementOffset,
      droppableContainerRef,
      shouldFixDraggableElement,
      draggableElementParentRef,
      onDragEnd,
      fps
    ]
  );

  return { onMouseDown };
};
